// =============================================================================
// src/features/mapa-ia/mapa-ia-route.ts — Rota fullscreen do Mapa de IA.
//
// Proveniência: clona o contrato da BriefingFullscreenRoute (briefing-route.ts) —
//   body class própria, fecha as rotas irmãs (Briefing/Explorar/Grafo/Workspace) +
//   Cinema ao abrir, re-sincroniza a topbar. SEM mini-mapa (esta rota é só
//   documentação do contrato de IA), então não há BriefingMap/duplo-rAF/invalidate.
// =============================================================================

import { qs } from '../../app/dom';
import {
  getTopbarNav,
  getExploreRouteGlobal,
  getGrafoRoute,
  getWorkspaceRoute,
} from '../explore/explore-globals';

/** Classe no <body> que ativa o layout fullscreen do Mapa de IA. */
const MAPA_IA_VIEW_CLASS = 'is-mapa-ia-view';
/** Classe do Modo Cinema (removida ao abrir). */
const CINEMA_ACTIVE_CLASS = 'cinema-active';

/** Rota Briefing global (fechada ao abrir; explore-globals não a expõe). */
interface BriefingRouteGlobal {
  isOpen(): boolean;
  close(): void;
}
interface MapaIaWindow {
  BriefingRoute?: BriefingRouteGlobal;
  AnalyticsRoute?: BriefingRouteGlobal;
}

/** Controlador da rota Mapa de IA (open/close/init). Singleton exportado ao final. */
class MapaIaFullscreenRoute {
  private page: HTMLElement | null = null;

  /** Resolve a página uma vez (mesmo contrato da BriefingRoute.init). */
  init(): void {
    this.page = qs<HTMLElement>('#u-mapa-ia-page');
  }

  /** True quando o Mapa de IA está visível. */
  isOpen(): boolean {
    return document.body.classList.contains(MAPA_IA_VIEW_CLASS);
  }

  /** Abre a rota: fecha as irmãs + Cinema, ativa o layout, sincroniza a topbar. */
  open(): void {
    if (this.isOpen()) return;
    this.closeSiblingRoutes();
    document.body.classList.remove(CINEMA_ACTIVE_CLASS);
    document.body.classList.add(MAPA_IA_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'false');
    getTopbarNav()?.syncActive();
  }

  /** Fecha Briefing/Explorar/Grafo/Workspace se estiverem abertas. */
  private closeSiblingRoutes(): void {
    const win = window as unknown as MapaIaWindow;
    const briefing = win.BriefingRoute;
    const analytics = win.AnalyticsRoute;
    if (briefing?.isOpen()) briefing.close();
    if (analytics?.isOpen()) analytics.close();
    const explore = getExploreRouteGlobal();
    if (explore?.isOpen()) explore.close();
    const grafo = getGrafoRoute();
    if (grafo?.isOpen?.()) grafo.close();
    const workspace = getWorkspaceRoute();
    if (workspace?.isOpen?.()) workspace.close();
  }

  /** Fecha a rota e re-sincroniza a topbar. */
  close(): void {
    if (!this.isOpen()) return;
    document.body.classList.remove(MAPA_IA_VIEW_CLASS);
    this.page?.setAttribute('aria-hidden', 'true');
    getTopbarNav()?.syncActive();
  }
}

/** Singleton da rota Mapa de IA — publicado em window.MapaIaRoute por index.ts. */
export const MapaIaRoute = new MapaIaFullscreenRoute();
